import type { SubscriptionStatus } from '~/services/subscription/getSubscriptionStatus';
import { formatAmount } from '~/utils/trialPricing';
import { ANNUAL_PRICE_CENTS, MONTHLY_LIST_CENTS } from '~/utils/annualOffer';

/**
 * Formule Pro (« Ma marque » : logo + couleurs sur la TV et les téléphones), décidée par
 * Victorien le 26/10/2026 : 69 €/mois. Même prix Stripe multi-devises que le mensuel
 * standard (49 € / 59 $ / 45 £).
 * Pro ANNUEL « 12 mois pour le prix de 10 » (validé le 29/10/2026) : 690 € / 790 $ / 590 £ par an.
 * Passage en 1 clic : portail Stripe sur l'écran de confirmation (stripe-b2b, `intent=pro` ou
 * `intent=pro_annual`). En essai, le portail (réglage `trial_update_behavior: end_trial`, comme
 * pour l'annuel) TERMINE l'essai et prélève tout de suite : la carte le dit (`trialing`).
 */
export const PRO_MONTHLY_CENTS: Record<string, number> = { eur: 6900, usd: 7900, gbp: 5900 };
export const PRO_ANNUAL_CENTS: Record<string, number> = { eur: 69000, usd: 79000, gbp: 59000 };

/**
 * Formule actuelle d'où part le passage en Pro (change le texte et ce qui est proposé).
 * `ended` : plus d'abonnement (essai fini, résilié) → stripe-b2b ouvre la page de paiement
 * d'un nouvel abonnement Pro (`intent=pro` / `pro_annual`, même bouton).
 */
export type ProFrom = 'monthly' | 'annual' | 'pro_monthly' | 'ended';

export type ProOffer = {
    from: ProFrom;
    /** Passage au Pro mensuel (depuis le mensuel standard, ou reprise d'un abonnement fini). */
    monthly: { pro: string; current: string } | null;
    /** Passage au Pro annuel : prix par an et 12 mois de Pro mensuel (pour « 2 mois offerts »). */
    annual: { price: string; twelveMonths: string };
    trialing: boolean;
    /** Pro à l'année refusé par le serveur : seulement le mensuel. */
    yearlyOff?: boolean;
};

/**
 * Passage en Pro proposable : abonnement en essai ou actif, au prix catalogue, sans résiliation,
 * pause, calendrier ni remise programmés (le portail Stripe refuserait le changement ; une remise
 * partenaire sauterait au changement de prix). Départs possibles (mêmes règles que stripe-b2b,
 * proPlan.js) :
 * - mensuel standard → Pro mensuel ou Pro annuel ;
 * - annuel standard → Pro annuel (Stripe prélève la différence au prorata de l'année en cours) ;
 * - Pro mensuel → Pro annuel.
 */
export function proOfferFor(sub: SubscriptionStatus | null, lang: string): ProOffer | null {
    if (!sub) return null;
    if (typeof sub.intervalCount === 'number' && sub.intervalCount !== 1) return null;
    if (sub.pauseReason === 'has_schedule') return null;
    if (sub.status !== 'active' && sub.status !== 'trialing') return null;
    if (sub.cancelAtPeriodEnd || sub.cancelAt) return null;
    if (sub.pause && sub.pause.status === 'paused') return null;
    if (sub.discounted) return null;
    const currency = (sub.currency || '').toLowerCase();
    const proMonthly = PRO_MONTHLY_CENTS[currency];
    const proAnnual = PRO_ANNUAL_CENTS[currency];
    const monthly = MONTHLY_LIST_CENTS[currency];
    if (!proMonthly || !proAnnual || !monthly) return null;

    let from: ProFrom | null = null;
    if (sub.interval === 'month' && sub.nextAmount === monthly) from = 'monthly';
    else if (sub.interval === 'month' && sub.nextAmount === proMonthly) from = 'pro_monthly';
    else if (sub.interval === 'year' && sub.nextAmount === ANNUAL_PRICE_CENTS[currency]) from = 'annual';
    if (!from) return null;

    return {
        from,
        monthly:
            from === 'monthly'
                ? { pro: formatAmount(proMonthly, currency, lang), current: formatAmount(monthly, currency, lang) }
                : null,
        annual: {
            price: formatAmount(proAnnual, currency, lang),
            twelveMonths: formatAmount(proMonthly * 12, currency, lang),
        },
        trialing: sub.status === 'trialing',
    };
}

/** Statuts Stripe d'un lieu qui n'a plus d'abonnement en cours (`none` : aucun). */
const ENDED_STATUSES = ['canceled', 'incomplete_expired', 'none'];

/**
 * Lieu sans abonnement en cours : reprise directement en Pro, au mois ou à l'année (page de
 * paiement Stripe). Devise : celle de l'ancien abonnement, sinon l'estimation de la région
 * (`fallbackCurrency`) — Stripe affiche de toute façon le vrai montant.
 */
export function proEndedOfferFor(
    sub: SubscriptionStatus | null,
    lang: string,
    fallbackCurrency: string
): ProOffer | null {
    if (!sub || !ENDED_STATUSES.includes(sub.status)) return null;
    const currency = (sub.currency || fallbackCurrency || '').toLowerCase();
    const proMonthly = PRO_MONTHLY_CENTS[currency];
    const proAnnual = PRO_ANNUAL_CENTS[currency];
    const monthly = MONTHLY_LIST_CENTS[currency];
    if (!proMonthly || !proAnnual || !monthly) return null;
    return {
        from: 'ended',
        monthly: { pro: formatAmount(proMonthly, currency, lang), current: formatAmount(monthly, currency, lang) },
        annual: {
            price: formatAmount(proAnnual, currency, lang),
            twelveMonths: formatAmount(proMonthly * 12, currency, lang),
        },
        trialing: false,
    };
}

/**
 * Pourquoi la carte « Ma marque » ne propose pas le bouton « Passer en Pro », pour dire quoi
 * faire à la place (jamais « écrivez-nous » quand un geste suffit) :
 * - `member` : seul l'admin change de formule ;
 * - `cancel_scheduled` : arrêt programmé → « Garder Unboared » d'abord (rien n'est prélevé) ;
 * - `paused` : pause en cours ou programmée → la reprendre (rubrique Abonnement) ;
 * - `payment_issue` : paiement refusé → mettre la carte à jour (portail Stripe) ;
 * - `discount` : tarif partenaire, que le changement de prix ferait sauter → contact ;
 * - `error` : statut illisible (serveur injoignable) ;
 * - `other` : cas rare (plusieurs abonnements, calendrier, formule hors catalogue) → contact.
 * À n'appeler que si ni `proOfferFor` ni `proEndedOfferFor` ne propose rien.
 */
export type ProBlock = 'member' | 'cancel_scheduled' | 'paused' | 'payment_issue' | 'discount' | 'error' | 'other';

export function proBlockFor(sub: SubscriptionStatus | null): ProBlock {
    if (!sub) return 'error';
    if (sub.viewerRole === 'member') return 'member';
    // `paused` côté Stripe = essai fini sans moyen de paiement : le geste est d'ajouter une carte.
    if (['past_due', 'unpaid', 'paused'].includes(sub.status)) return 'payment_issue';
    // Arrêt programmé, pause : on ne promet le bouton Pro que si lever l'arrêt (ou la pause) le
    // débloque vraiment (pas un tarif partenaire ni une formule hors catalogue derrière).
    // Arrêt programmé : Stripe ne prévoit plus de facture, `nextAmount` est vide. Sans remise, la
    // prochaine facture une fois l'abonnement gardé est le prix catalogue de sa périodicité.
    const currency = (sub.currency || '').toLowerCase();
    const catalog = sub.interval === 'year' ? ANNUAL_PRICE_CENTS[currency] : MONTHLY_LIST_CENTS[currency];
    const lifted: SubscriptionStatus = {
        ...sub,
        nextAmount: sub.nextAmount ?? (sub.discounted ? null : catalog),
        cancelAtPeriodEnd: false,
        cancelAt: null,
        pause: null,
    };
    const unlocks = !!proOfferFor(lifted, 'fr');
    if (unlocks && sub.pause && sub.pause.status === 'paused') return 'paused';
    if (unlocks && (sub.cancelAtPeriodEnd || sub.cancelAt)) return 'cancel_scheduled';
    if (sub.discounted) return 'discount';
    return 'other';
}

/**
 * Offre Pro réduite à ce que le serveur accepterait vraiment (`planOptions` de
 * `/subscription-status`) : pas de bouton voué au refus (calendrier, changement en attente,
 * plusieurs abonnements…). Sans `planOptions` (ancien serveur), l'offre locale telle quelle.
 */
export function gateProOffer(offer: ProOffer | null, options: SubscriptionStatus['planOptions']): ProOffer | null {
    if (!offer || !options) return offer;
    if (offer.from === 'ended') return options.ended ? offer : null;
    const yearly = options.upgrades.includes('pro_annual');
    const monthly = options.upgrades.includes('pro') ? offer.monthly : null;
    if (!yearly && !monthly) return null;
    // Sans le Pro à l'année, le choix ne garde que le mensuel (et inversement).
    return yearly ? { ...offer, monthly } : { ...offer, yearlyOff: true, monthly };
}
